// One lesson in a course's ordered list (Attio-style row): number, a small
// thumbnail with a play or cursor glyph, title + summary + chips, duration,
// a "View" button, and a completed check that toggles without opening.
import { CheckCircleIcon as CheckCircleOutline } from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import cn from "classnames";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import Tooltip from "../../components/common/Tooltip";
import { useAuth } from "../../context/AuthContext";
import { Course, LessonDefinition } from "../catalog/types";
import { useAcademyProgress } from "../progress";
import { GateChip, NewChip, PlatformChip, WalkthroughChip, WatchChip } from "./Chip";
import CourseThumbnail from "./CourseThumbnail";
import { useGateStatus } from "./gates";
import { isNewForUser, lessonPath } from "./LessonCard";

const LessonRow: React.FC<{ lesson: LessonDefinition; course: Course; index: number; className?: string }> = ({ lesson, course, index, className }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isCompleted, isViewed, toggleCompleted } = useAcademyProgress();
  const gate = useGateStatus(lesson.gate);
  const done = isCompleted(lesson.id);
  const isNew = isNewForUser(lesson, (user as any)?.created_at, isViewed(lesson.id));
  const open = () => navigate(lessonPath(lesson));

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter") open();
      }}
      className={cn(
        "group flex items-center gap-3 p-3 rounded-lg border bg-base-100 cursor-pointer transition-all",
        done ? "border-base-300" : "border-base-300",
        "hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40",
        className,
      )}
    >
      <span className="w-5 shrink-0 text-right text-xs tabular-nums text-base-content/50">{index + 1}</span>
      <CourseThumbnail course={course} size="row" glyph={lesson.sceneId ? "watch" : lesson.tourId ? "walkthrough" : null} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={cn("m-0 font-medium text-sm truncate transition-colors", done ? "text-base-content/70" : "text-base-content", "group-hover:text-blue-600 dark:group-hover:text-blue-300")}>{lesson.title}</h3>
          {lesson.subtitle && <span className="hidden md:inline truncate text-sm text-base-content/50">{lesson.subtitle}</span>}
          {isNew && <NewChip />}
        </div>
        <p className="m-0 mt-0.5 text-xs text-base-content/60 line-clamp-2">{lesson.summary}</p>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {lesson.sceneId && <WatchChip />}
          {lesson.tourId && <WalkthroughChip />}
          {lesson.platform && <PlatformChip platform={lesson.platform} />}
          {gate && <GateChip label={gate.label} locked={gate.locked} reason={gate.reason} />}
        </div>
      </div>
      <span className="hidden sm:block shrink-0 text-xs tabular-nums text-base-content/60 w-12 text-right">{lesson.minutes} min</span>
      <Button
        variant="secondary"
        size="sm"
        className="hidden sm:inline-flex shrink-0 px-3"
        onClick={(e) => {
          e.stopPropagation();
          open();
        }}
      >
        View
      </Button>
      <Tooltip overlay={done ? "Mark as not completed" : "Mark as completed"} placement="left">
        <button
          type="button"
          aria-pressed={done}
          aria-label={done ? "Completed" : "Mark as completed"}
          onClick={(e) => {
            e.stopPropagation();
            toggleCompleted(lesson.id);
          }}
          className={cn("shrink-0 rounded-full p-0.5 transition-colors", done ? "text-blue-500 hover:text-blue-600" : "text-base-content/30 hover:text-blue-500")}
        >
          {done ? <CheckCircleSolid className="h-6 w-6" /> : <CheckCircleOutline className="h-6 w-6" />}
        </button>
      </Tooltip>
    </div>
  );
};

export default LessonRow;
